import type { ReactNode } from 'react';

import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import type { LegalDocumentFamily } from '@alamo/api';
import { PermissionGuard, usePermissions } from '@alamo/auth';

import {
  LEGAL_DOCUMENTS_PATH,
  LEGAL_DOCUMENT_FAMILIES,
  canManageLegalDocuments,
  legalDocumentManagementPath,
  legalDocumentEditPath,
} from './legalDocumentsNavigation';

/** The entry opens the first authorized CRUD; catalog-only readers retain consultation. */
export function LegalDocumentsEntry({ children }: { children: ReactNode }) {
  const { hasPermission } = usePermissions();
  const [params] = useSearchParams();
  const requested = params.get('family');
  const family = LEGAL_DOCUMENT_FAMILIES.find((value) =>
    canManageLegalDocuments(value, hasPermission),
  );
  const requestedFamily = LEGAL_DOCUMENT_FAMILIES.find((value) => value === requested);
  const target = requestedFamily ?? family;
  if (
    params.get('view') !== 'catalog' &&
    target &&
    canManageLegalDocuments(target, hasPermission)
  ) {
    const url = new URL(legalDocumentManagementPath(target), 'https://alamo.local');
    for (const key of ['search', 'limit', 'page']) {
      const value = params.get(key);
      if (value) url.searchParams.set(key, value);
    }
    return <Navigate to={url.pathname + url.search} replace />;
  }
  return (
    <PermissionGuard resource="legal_terms" action="read">
      {children}
    </PermissionGuard>
  );
}

export function LegacyLegalDocumentsRedirect() {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  const workflow = next.get('view') === 'workflow';
  if (workflow) next.delete('view');
  return (
    <Navigate
      to={`${LEGAL_DOCUMENTS_PATH}${workflow ? '/aceites' : ''}${next.size ? `?${next}` : ''}`}
      replace
    />
  );
}

/** Existing bookmarks resolve to the same legal document workspace. */
export function LegacyLegalDocumentManagementRedirect({
  family,
  documentId,
}: {
  family: LegalDocumentFamily;
  documentId?: string;
}) {
  const { id } = useParams();
  const [params] = useSearchParams();
  const activeFamily =
    family === 'associate' && params.get('scope') === 'refund' ? 'refund' : family;
  const targetId = documentId ?? id;
  const target = targetId
    ? legalDocumentEditPath(activeFamily, targetId)
    : legalDocumentManagementPath(activeFamily);
  const url = new URL(target, 'https://alamo.local');
  for (const [key, value] of params) url.searchParams.set(key, value);
  return <Navigate to={url.pathname + url.search} replace />;
}
